import React, { useContext, useEffect, useState } from "react";
import {
  Loader,
  TextButton,
  ModalContext,
  ModalButtonsContainer,
  useMutationWrapper,
  useIcon,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import {
  DELETE_FILTER,
  type DeleteFilterResponse,
} from "../../../Findings/FindingsFilters/FindingsFilterApi";
import { useFilterDependencies } from "./useFilterDependencies";
import DependenciesModal from "../../Dependencies/DependenciesModal";
import type { DependencySet } from "../../Dependencies/types";
import "./FilterDependencies.scss";

export interface DeleteFilterModalProps {
  filterId: string;
  filterName?: string;
  onDeleted: () => void;
}

/**
 * Delete-filter flow (one modal, three states):
 *   1. checking connections (loading)
 *   2. not in use  → confirmation → deleteFindingFilterV3 → onDeleted
 *   3. in use      → blocker with the referencing rules / automations / widgets
 */
const DeleteFilterModal: React.FC<DeleteFilterModalProps> = ({
  filterId,
  filterName,
  onDeleted,
}) => {
  const { closeModal } = useContext(ModalContext);
  const { getSvg } = useIcon();
  const { check, loading, called, dependencies, error } =
    useFilterDependencies();
  const [deleteFilter, { loading: deleting }] =
    useMutationWrapper<DeleteFilterResponse>(DELETE_FILTER, {
      refetchQueries: ["filters"],
    });

  const [blockedDependencies, setBlockedDependencies] =
    useState<DependencySet | null>(null);
  const activeDependencies = blockedDependencies ?? dependencies;

  useEffect(() => {
    if (filterId) {
      check(filterId);
    }
  }, [filterId, check]);

  const recheck = () => {
    setBlockedDependencies(null);
    check(filterId);
  };

  const confirmDelete = () => {
    deleteFilter({ variables: { id: filterId } })
      .then((res) => {
        const payload = res?.data?.delete_finding_filter_v3;
        if (!payload?.ok) {
          setBlockedDependencies(payload?.dependencies ?? null);
          return;
        }
        onDeleted();
        closeModal();
      })
      .catch((err: { message?: string }) =>
        showErrorDialog({ message: err.message })
      );
  };

  if (!called || loading) {
    return (
      <div className={"modal-text delete-filter-modal is-centered"}>
        <Loader
          center
          img={getSvg("icon-spinner")}
          fullHeight
          iconSize={"xxxxl"}
          title={"Checking connections..."}
          subtitle={
            "Please wait while we verify if this filter is linked to any items."
          }
        />
      </div>
    );
  }

  // Show the error/retry state on an actual error, or when the check resolved with
  // no dependencies object at all (server returned null) — i.e. we couldn't tell.
  if (!blockedDependencies && (error || !dependencies)) {
    return (
      <div className={"modal-text delete-filter-modal is-centered"}>
        <Loader
          center
          title={"Couldn't check this filter's connections."}
          subtitle={"Please try again."}
        />
        <ModalButtonsContainer centerButtons>
          <TextButton
            buttonStyle={"secondary"}
            label={"Close"}
            onClick={closeModal}
            data-testid={"close-delete-filter"}
          />
          <TextButton
            buttonStyle={"primary"}
            label={"Retry"}
            onClick={recheck}
            data-testid={"retry-filter-dependencies"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  if (activeDependencies && activeDependencies.total_count > 0) {
    return (
      <div className={"delete-filter-modal"}>
        <DependenciesModal
          entityName={filterName}
          dependencies={activeDependencies}
        />
        <ModalButtonsContainer spaceBetween>
          <TextButton
            buttonStyle={"textLink"}
            leftIcon={"seem-icon-refresh"}
            label={"Refresh the Data"}
            onClick={recheck}
            data-testid={"refresh-filter-dependencies"}
          />
          <TextButton
            buttonStyle={"secondary"}
            label={"Close"}
            onClick={closeModal}
            data-testid={"close-delete-filter"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  return (
    <div className={"modal-text delete-filter-modal is-centered is-confirm"}>
      <div className={"margin-bottom-20 delete-filter-confirm"}>
        {"Are you sure you want to delete "}
        <span className={"modal-live-text"}>{filterName}</span>
        {" filter?"}
      </div>
      <ModalButtonsContainer centerButtons>
        <TextButton
          buttonStyle={"secondary"}
          label={"Cancel"}
          onClick={closeModal}
          disabled={deleting}
          data-testid={"cancel-delete"}
        />
        <TextButton
          buttonStyle={"danger"}
          label={"Delete"}
          onClick={confirmDelete}
          disabled={deleting}
          data-testid={"confirm-delete"}
        />
      </ModalButtonsContainer>
    </div>
  );
};

export default DeleteFilterModal;
